/**
 * useAllMcpTools - Hook for fetching tools from ALL MCP services
 * 
 * Blue Steel Excellence: Unified tool browsing across ALL servers
 * - Fetches tools from all services in parallel
 * - Provides filtering by service, search, category
 * - Groups tools by service for organized display
 * 
 * @example
 * ```tsx
 * const { tools, loading, error } = useAllMcpTools();
 * ```
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import type { MCPTool } from '../types';
import { useAllMcpServices, MCPService } from './useAllMcpServices';

export interface MCPToolWithOrigin extends MCPTool {
  service_id: string;
  service_name: string;
  service_display_name: string;
}

interface UseAllMcpToolsOptions {
  search?: string;
  serviceFilter?: string | null;
  category?: string | null;
}

interface UseAllMcpToolsResult {
  tools: MCPToolWithOrigin[];
  filteredTools: MCPToolWithOrigin[];
  toolsByService: Map<string, MCPToolWithOrigin[]>;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
  categories: string[];
  services: MCPService[];
}

// In-memory cache per service with 3-minute TTL
const toolsCache = new Map<string, { data: MCPTool[]; timestamp: number }>();
const CACHE_TTL_MS = 3 * 60 * 1000;

// Debounce helper
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

async function fetchToolsForService(serviceId: string): Promise<MCPTool[]> {
  // Check cache
  const cached = toolsCache.get(serviceId);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  const response = await fetch(
    `/api/v1/mcp/services/${encodeURIComponent(serviceId)}/tools`,
    { credentials: 'include' }
  );

  if (!response.ok) {
    console.warn(`[useAllMcpTools] Failed to fetch tools for ${serviceId}: ${response.statusText}`);
    return [];
  }

  const data = await response.json();
  // API returns { tools: [...] } or array directly
  const tools: MCPTool[] = data.tools || data || [];

  // Update cache
  toolsCache.set(serviceId, { data: tools, timestamp: Date.now() });

  return tools;
}

export function useAllMcpTools(options: UseAllMcpToolsOptions = {}): UseAllMcpToolsResult {
  const { search = '', serviceFilter, category } = options;
  const { services, loading: servicesLoading, refresh: refreshServices } = useAllMcpServices();
  const [tools, setTools] = useState<MCPToolWithOrigin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Debounce search term
  const debouncedSearch = useDebounce(search, 300);

  const refresh = useCallback(() => {
    toolsCache.clear();
    refreshServices();
    setRefreshKey(prev => prev + 1);
  }, [refreshServices]);

  useEffect(() => {
    if (servicesLoading) {
      setLoading(true);
      return;
    }

    if (services.length === 0) {
      setTools([]);
      setLoading(false);
      return;
    }

    let mounted = true;

    const fetchAllTools = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch tools from ALL services in parallel
        const results = await Promise.all(
          services.map(async (service) => {
            const serviceTools = await fetchToolsForService(service.id);
            return serviceTools.map(tool => ({
              ...tool,
              service_id: service.id,
              service_name: service.name,
              service_display_name: service.display_name || service.name,
            }));
          })
        );

        // Flatten all tools
        const allTools = results.flat();

        if (mounted) {
          setTools(allTools);
          setLoading(false);
        }
      } catch (err) {
        console.error('[useAllMcpTools] Error:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    };

    fetchAllTools();

    return () => {
      mounted = false;
    };
  }, [services, servicesLoading, refreshKey]);

  // Extract unique categories (now includes normalized tags from backend)
  const categories = useMemo(() => {
    const categorySet = new Set<string>();
    tools.forEach(tool => {
      // Add from categories
      tool.categories?.forEach(cat => categorySet.add(cat));
      // Also add from normalized tags field (consolidated by backend)
      tool.tags?.forEach(tag => categorySet.add(tag));
    });
    return Array.from(categorySet).sort();
  }, [tools]);

  // Group tools by service
  const toolsByService = useMemo(() => {
    const map = new Map<string, MCPToolWithOrigin[]>();
    tools.forEach(tool => {
      const existing = map.get(tool.service_id) || [];
      existing.push(tool);
      map.set(tool.service_id, existing);
    });
    return map;
  }, [tools]);

  // Apply filters
  const filteredTools = useMemo(() => {
    let results = tools;

    // Service filter
    if (serviceFilter) {
      results = results.filter(tool => tool.service_id === serviceFilter);
    }

    // Search filter (includes normalized fields from backend)
    if (debouncedSearch) {
      const term = debouncedSearch.toLowerCase();
      results = results.filter(tool =>
        tool.name.toLowerCase().includes(term) ||
        (tool.description || '').toLowerCase().includes(term) ||
        tool.service_display_name.toLowerCase().includes(term) ||
        // Search in tags (normalized by backend)
        tool.tags?.some(tag => tag.toLowerCase().includes(term)) ||
        // Search in source type
        (tool.source || '').toLowerCase().includes(term) ||
        // Search in provider
        (tool.metadata?.provider || '').toLowerCase().includes(term)
      );
    }

    // Category filter
    if (category) {
      results = results.filter(tool => tool.categories?.includes(category));
    }

    return results;
  }, [tools, debouncedSearch, serviceFilter, category]);

  return {
    tools,
    filteredTools,
    toolsByService,
    loading,
    error,
    refresh,
    categories,
    services,
  };
}

/**
 * Clear the tools cache
 */
export function clearAllToolsCache(): void {
  toolsCache.clear();
}

